import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { SheetSearchField } from "../../../UIToolkit/Inputs/SheetSearchField";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { GapsRoleBadge } from "../GapsRoleBadge";
import { findGroupByKey, findStaffPlacement, type GapsScopedShiftGroup, type GapsStaffPlacement } from "../gapsGroupSheets";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import type { GapsHomeModel } from "../useGapsDayModel";
import { closeGapsSheetIfCurrent, type GapsSheetIntent, type GapsSheetIntentOf, type GapsSheetKind } from "../useGapsSheet";
import { GapsCoverPromptStep } from "./GapsCoverPromptStep";
import { GapsDateField } from "./GapsDateField";
import { GapsRemovalStep } from "./GapsRemovalStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { type GapsRemovalOutcome, GapsRemovalSource } from "./gapsSheetTypes";
import { staffHoursLabel } from "./staffSheetCopy";
import { useOpeningStep } from "./useOpeningStep";
import { useSheetOpening } from "./useSheetOpening";

type Props = {
	/** The store's own intent object, or `null` when the sheet is not up (see `useSheetOpening`). */
	target: GapsSheetIntentOf<GapsSheetKind.CallIn> | null;
	home: GapsHomeModel;
	/** The day the home is showing — whose schedule the search lists. */
	date: DateTime;
	/** The cover prompt's Post, prefilled from the group the removal left short; `undefined` on a day that is over. */
	onPostShift: ((groupKey: string) => void) | undefined;
	/** The pick step's Date row: moves this opening, and the home under it, to another day (APP-886). */
	onMoveDay: (from: GapsSheetIntent, date: DateTime) => void;
};

enum CallInStep {
	Pick = "pick",
	Remove = "remove",
	Cover = "cover",
}

const SHEET_TEST_ID: Record<CallInStep, string> = {
	[CallInStep.Pick]: "gaps-call-in-sheet",
	[CallInStep.Remove]: "gaps-removal-sheet",
	[CallInStep.Cover]: "gaps-cover-prompt-sheet",
};

const SHEET = "gaps-call-in";

type CallInState = { step: CallInStep; placement: GapsStaffPlacement<GapsScopedShiftGroup> | null; outcome: GapsRemovalOutcome | null };
const START: CallInState = { step: CallInStep.Pick, placement: null, outcome: null };

/**
 * Log a call-in (More → Quick actions): find who called in, then the same removal step a staff row's Remove opens,
 * then the cover prompt (P3-3, P3-4).
 *
 * The search lists the day's staff UNNARROWED (`home.callInStaff`, each shift once): a call-in is the day's record,
 * not a view of it, so the saved scope does not reach it (P2-11) — a supervisor scoped to three carts can still log
 * a call-in for anyone in the building. Each person is described by the group they are placed by, the same group the
 * removal and the cover prompt then read.
 *
 * The page belongs to the opening (`useOpeningStep`): a removal that finishes after the user closed this sheet and opened
 * it again (or another from the bar) does not put its cover prompt into the newer opening.
 */
export const GapsCallInSheet: FC<Props> = ({ target, home, date, onPostShift, onMoveDay }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [search, setSearch] = useState<string>("");
	const deferredSearch = useDeferredValue(search);
	const open = target !== null;
	const { shown: opening, openingKey } = useSheetOpening(target);
	const { step: state, setStepFor } = useOpeningStep<CallInState>(openingKey, START);
	// Closes THIS opening only: a removal that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (opening !== null) closeGapsSheetIfCurrent(opening);
	}, [opening]);

	// Every opening starts from the search (`useOpeningStep`), and with the field empty — adjusted during render so the
	// first frame is already right.
	const [searchFor, setSearchFor] = useState<number | null>(openingKey);
	if (open && openingKey !== searchFor) {
		setSearchFor(openingKey);
		setSearch("");
	}

	const candidates = useMemo(
		() =>
			home.callInStaff.flatMap((entry) => {
				const placement = findStaffPlacement(home.dayGroups, entry.slot.nurseInShift.id);
				return placement === undefined ? [] : [placement];
			}),
		[home.callInStaff, home.dayGroups],
	);
	const query = deferredSearch.trim().toLowerCase();
	const matches = useMemo(
		() => (query === "" ? candidates : candidates.filter(({ entry }) => entry.slot.name.toLowerCase().includes(query))),
		[candidates, query],
	);

	const handlePick = useCallback(
		(placement: GapsStaffPlacement<GapsScopedShiftGroup>): void => {
			track("gaps_call_in_staff_picked", {
				department_id: chosenDepartment.department_id,
				shift_id: placement.entry.slot.nurseInShift.id,
				searched: query !== "",
			});
			setStepFor(openingKey, { step: CallInStep.Remove, placement, outcome: null });
		},
		[chosenDepartment.department_id, openingKey, query, setStepFor, track],
	);

	// Made for THIS render's opening: the removal calls it after its own await (see `useOpeningStep`).
	const handleRemoved = useCallback(
		(outcome: GapsRemovalOutcome): void => {
			setStepFor(openingKey, { step: CallInStep.Cover, placement: null, outcome });
		},
		[openingKey, setStepFor],
	);

	const dayLabel = date.toFormat(GAPS_DAY_LABEL_FORMAT);

	const renderStep = () => {
		if (state.step === CallInStep.Remove && state.placement !== null) {
			const { group, entry } = state.placement;
			const displayed = findGroupByKey(home.groups, home.dayGroups, group.key);
			return (
				<GapsRemovalStep
					nurseInShift={entry.slot.nurseInShift}
					isActual={entry.slot.isActual}
					name={entry.slot.name}
					group={group}
					hoursLabel={staffHoursLabel(entry, group.timeLabel)}
					totalsBefore={displayed === undefined ? null : { actual: displayed.actual, required: displayed.required }}
					source={GapsRemovalSource.CallIn}
					onBack={() => setStepFor(openingKey, START)}
					onRemoved={handleRemoved}
				/>
			);
		}
		if (state.step === CallInStep.Cover && state.outcome !== null) {
			return (
				<GapsCoverPromptStep
					outcome={state.outcome}
					group={findGroupByKey(home.groups, home.dayGroups, state.outcome.groupKey)}
					onPostShift={onPostShift}
					onClose={onClose}
				/>
			);
		}
		return (
			<SheetStep
				stepKey={CallInStep.Pick}
				dataTestId={SHEET}
				title={t("gaps.callIn.pick.title")}
				subtitle={t("gaps.callIn.pick.subtitle", { day: dayLabel })}
				// Pinned with the heading, so the field stays in reach while the list scrolls.
				toolbar={
					<>
						<SheetSearchField
							value={search}
							onChange={setSearch}
							placeholder={t("gaps.callIn.pick.search")}
							name="gaps-call-in-search"
							dataTestId={`${SHEET}-search`}
						/>
						<Typography data-testid={`${SHEET}-hits`} sx={{ mt: 0.75, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>
							{t("gaps.callIn.pick.hits", { count: matches.length, total: candidates.length })}
						</Typography>
					</>
				}
				footer={
					<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
						{t("cancel")}
					</SheetStepButton>
				}
			>
				{/* The day leads the list, as on Add to a shift (APP-886) — and past days can be picked: a call-off is often logged after the fact. */}
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<GapsDateField
						date={date}
						homeWeekShortfalls={home.isWeekReady ? home.weekShortfalls : undefined}
						onPick={target === null ? undefined : (next) => onMoveDay(target, next)}
						allowsPast
						testIdPrefix={SHEET}
					/>
				</Box>
				<Box data-testid={`${SHEET}-list`}>
					{matches.map(({ group, entry }) => (
						<SheetActionRow
							key={entry.key}
							leading={<GapsRoleBadge role={entry.slot.nurseInShift.role} size="row" />}
							label={entry.slot.name}
							description={joinLabelParts([`${group.jobLabel} ${group.timeLabel}`, entry.locationName])}
							trailing={staffHoursLabel(entry, group.timeLabel)}
							showChevron
							onClick={() => handlePick({ group, entry })}
							dataTestId={`${SHEET}-option-${entry.slot.nurseInShift.id}`}
						/>
					))}
					{candidates.length === 0 && <EmptyState dataTestId={`${SHEET}-nobody`} title={t("gaps.callIn.pick.nobody")} sx={{ py: 4 }} />}
					{candidates.length > 0 && matches.length === 0 && (
						<EmptyState dataTestId={`${SHEET}-no-matches`} title={t("gaps.callIn.pick.noMatches", { query: deferredSearch.trim() })} sx={{ py: 4 }} />
					)}
				</Box>
			</SheetStep>
		);
	};

	return (
		<GapsSheetFrame open={open} onClose={onClose} dataTestId={SHEET_TEST_ID[state.step]}>
			{renderStep()}
		</GapsSheetFrame>
	);
};
